<template>
  <FlexCol ref="selectContainer" gap-1 class="relative">
    <Label :label="label" :required="required" :error="error" v-if="label" />
    <InputArc :error="error">
      <template #input>
        <div @click="toggle()" class="cursor-pointer">
          <span v-if="safeLocalValue.length > 0">{{ safeLocalValue.length }} Selecionado(s)</span>
          <span v-else class="italic"> Selecione </span>
        </div>
      </template>
      <template #pos-icon>
        <IconInput icon="fa-xmark" class="border-l" @click="clear()" v-if="!disableClear && safeLocalValue.length > 0" />
        <IconInput :icon="open ? 'fa-chevron-up' : 'fa-chevron-down'" class="border-l" @click="toggle()" />
      </template>
    </InputArc>
    <Error :message="error" v-if="error" />

    <Teleport to="body">
      <TheTransition v-if="open">
        <div ref="popupContainer" v-if="open" :style="popupStyle" class="fixed z-[2000]">
          <Card rounded border noSpace class="max-h-[168px] overflow-y-scroll text-sm border-slate-300">
            <Flex justify-start items-center>
              <InputCheck dense :checked="allItemsMarked" @click="selectAll(props.items)" status="Selecionar todos" />
            </Flex>
            <Flex justify-start items-center v-for="(item, i) in items" :key="i" class="hover:bg-slate-100 cursor-pointer" @click="addItem(item)">
              <InputCheck dense :status="item[itemText]" disable-action :checked="checked(item)" />
            </Flex>
          </Card>
        </div>
      </TheTransition>
    </Teleport>
  </FlexCol>
</template>

<script setup>
import { watch, ref, computed, onBeforeUnmount, nextTick, onMounted } from 'vue'

const props = defineProps({
  label: String,
  required: Boolean,
  disabled: Boolean,
  error: Array,
  modelValue: Array,
  placeholder: String,
  items: Array,
  showId: Boolean,
  itemValue: { type: String, default: 'value' },
  itemText: { type: String, default: 'label' },
  disableClear: { type: Boolean, default: false },
})

const emits = defineEmits(['update:modelValue'])
const selectContainer = ref(null)
const popupContainer = ref(null)
const localValue = ref(props.modelValue ?? [])
const error = ref(props.error)
const open = ref(false)
const selectedItem = ref([])
const popupStyle = ref({})
const safeLocalValue = computed(() => (Array.isArray(localValue.value) ? localValue.value : []))

const allItemsMarked = computed(() => {
  const selectedValues = new Set(safeLocalValue.value)

  if (!props.items || props.items.length === 0) {
    return false
  }

  for (const item of props.items) {
    const itemVal = item[props.itemValue]
    if (itemVal !== undefined && !selectedValues.has(itemVal)) {
      return false
    }
  }
  return true
})
const selectAll = (list) => {
  const shouldAdd = !allItemsMarked.value

  const listValues = new Set(list.map((item) => item[props.itemValue]))

  if (shouldAdd) {
    const selectedValues = new Set(safeLocalValue.value)
    for (const value of listValues) {
      if (!selectedValues.has(value)) {
        localValue.value.push(value)
      }
    }
  } else {
    let selectedValuesLength = safeLocalValue.value.length - 1
    for (let i = selectedValuesLength; i >= 0; i--) {
      const currentValue = localValue.value[i]
      if (listValues.has(currentValue)) {
        localValue.value.splice(i, 1)
      }
    }
  }

  open.value = false
}

const toggle = () => {
  open.value = !open.value
  if (open.value) {
    nextTick(updatePopupPosition)
  }
}

const handleClickOutside = (event) => {
  const triggerElement = selectContainer.value?.$el ?? selectContainer.value
  const popupElement = popupContainer.value?.$el ?? popupContainer.value
  const clickedOnTrigger = triggerElement?.contains(event.target)
  const clickedOnPopup = popupElement?.contains(event.target)

  if (!clickedOnTrigger && !clickedOnPopup) {
    open.value = false
  }
}

const updatePopupPosition = () => {
  const triggerElement = selectContainer.value?.$el ?? selectContainer.value
  if (!open.value || !triggerElement || typeof triggerElement.getBoundingClientRect !== 'function') return

  const rect = triggerElement.getBoundingClientRect()
  popupStyle.value = {
    top: `${rect.bottom + 8}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  }
}

watch(open, (isOpen) => {
  if (isOpen) {
    nextTick(updatePopupPosition)
    setTimeout(() => document.addEventListener('click', handleClickOutside), 0)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})

onMounted(() => {
  window.addEventListener('resize', updatePopupPosition)
  window.addEventListener('scroll', updatePopupPosition, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', updatePopupPosition)
  window.removeEventListener('scroll', updatePopupPosition, true)
})

const clear = () => {
  selectedItem.value = []
  localValue.value = []
  toggle()
}

const addItem = (item) => {
  const itemVal = item[props.itemValue]
  const index = safeLocalValue.value.indexOf(itemVal)
  if (index === -1) {
    localValue.value.push(itemVal)
  } else {
    localValue.value.splice(index, 1)
  }

  if (Array.isArray(selectedItem.value)) {
    const selectedIndex = selectedItem.value.findIndex((i) => i[props.itemValue] === itemVal)

    if (selectedIndex === -1) {
      selectedItem.value.push(item)
    } else {
      selectedItem.value.splice(selectedIndex, 1)
    }
  } else {
    selectedItem.value = [item]
  }
}

const checked = (item) => {
  const itemVal = item[props.itemValue]
  const index = safeLocalValue.value.indexOf(itemVal)
  return index >= 0 ? true : false
}

watch(
  () => props.modelValue,
  (newValue) => {
    localValue.value = Array.isArray(newValue) ? newValue : []
  },
)

watch(
  () => props.error,
  (newValue) => {
    error.value = newValue
  },
)

watch(
  localValue,
  (newValue) => {
    emits('update:modelValue', newValue)
  },
  { deep: true },
)
</script>
